import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTransferInfo, acceptCollectionTransfer, TransferInfo } from '../services/collectionSharingService';
import { useAuth } from '../contexts/AuthContext';

const CollectionTransferPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { token: authToken, user } = useAuth();
  const isAuthenticated = user !== null;

  const [info, setInfo] = useState<TransferInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [collectionId, setCollectionId] = useState<number | null>(null);

  useEffect(() => {
    if (!token) return;
    getTransferInfo(token)
      .then(setInfo)
      .catch((err) => setError(err.message || 'Transfer not found or expired'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleAccept = async () => {
    if (!authToken || !token) return;
    try {
      setAccepting(true);
      setError(null);
      const result = await acceptCollectionTransfer(authToken, token);
      setCollectionId(result.collection_id);
      setDone(true);
    } catch (err: any) {
      setError(err.message || 'Failed to accept transfer');
    } finally {
      setAccepting(false);
    }
  };

  const handleSignUpThenAccept = () => {
    // Store the transfer token so we can resume after sign up
    sessionStorage.setItem('pending_transfer_token', token || '');
    navigate('/');
  };

  // After sign-in/sign-up, auto-accept if we have a pending token
  useEffect(() => {
    const pending = sessionStorage.getItem('pending_transfer_token');
    if (isAuthenticated && authToken && pending && pending === token && info && !done) {
      sessionStorage.removeItem('pending_transfer_token');
      handleAccept();
    }
  }, [isAuthenticated, authToken, info]); // eslint-disable-line

  const containerStyle: React.CSSProperties = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--color-bg, #f5f5f5)',
    padding: '24px',
    fontFamily: 'Arial, sans-serif',
  };

  const cardStyle: React.CSSProperties = {
    background: 'var(--color-bg-card, #fff)',
    borderRadius: '16px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
    padding: '36px 32px',
    maxWidth: '460px',
    width: '100%',
    textAlign: 'center',
  };

  const brandStyle: React.CSSProperties = {
    color: '#007AFF',
    fontSize: '20px',
    fontWeight: 700,
    marginBottom: '28px',
    display: 'block',
  };

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <span style={brandStyle}>Wandering Sock</span>
          <div style={{ color: '#666', fontSize: '15px' }}>Loading transfer details…</div>
        </div>
      </div>
    );
  }

  if (error && !done) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <span style={brandStyle}>Wandering Sock</span>
          <div style={{ fontSize: '40px', marginBottom: '16px' }}>⚠️</div>
          <h2 style={{ margin: '0 0 12px', fontSize: '20px' }}>Transfer Unavailable</h2>
          <p style={{ color: '#666', fontSize: '14px', lineHeight: 1.5 }}>{error}</p>
          <button
            onClick={() => navigate('/')}
            style={{ marginTop: '24px', padding: '10px 24px', background: '#007AFF', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: 600, cursor: 'pointer' }}
          >
            Go to Wandering Sock
          </button>
        </div>
      </div>
    );
  }

  if (done && collectionId) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <span style={brandStyle}>Wandering Sock</span>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎉</div>
          <h2 style={{ margin: '0 0 12px', fontSize: '22px' }}>Collection Claimed!</h2>
          <p style={{ color: '#555', fontSize: '14px', lineHeight: 1.6 }}>
            <strong>"{info?.collection_name}"</strong> is now in your account. You have full access to your inventory.
          </p>
          <button
            onClick={() => navigate('/')}
            style={{ marginTop: '28px', padding: '12px 32px', background: '#007AFF', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 600, cursor: 'pointer', width: '100%' }}
          >
            Open My Inventory
          </button>
        </div>
      </div>
    );
  }

  if (!info) return null;

  const collectionIcon = info.icon || '📦';
  const expiresDate = new Date(info.expires_at).toLocaleDateString();

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <span style={brandStyle}>Wandering Sock</span>

        {/* Collection icon */}
        <div style={{
          width: 72, height: 72, borderRadius: '50%',
          background: info.color || '#007AFF',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '32px', margin: '0 auto 20px',
        }}>
          {collectionIcon}
        </div>

        <h2 style={{ margin: '0 0 6px', fontSize: '22px' }}>"{info.collection_name}"</h2>
        <p style={{ margin: '0 0 20px', color: '#666', fontSize: '14px' }}>
          Transferred to you by <strong>{info.from_name}</strong>
        </p>

        {info.message && (
          <div style={{
            background: '#f0f9ff', borderLeft: '4px solid #007AFF',
            padding: '12px 16px', borderRadius: '0 8px 8px 0',
            marginBottom: '20px', textAlign: 'left',
          }}>
            <p style={{ margin: 0, fontStyle: 'italic', color: '#555', fontSize: '14px' }}>"{info.message}"</p>
          </div>
        )}

        <p style={{ color: '#555', fontSize: '14px', lineHeight: 1.6, marginBottom: info.item_count > 50 ? '12px' : '24px' }}>
          This collection contains <strong>{info.item_count} item{info.item_count !== 1 ? 's' : ''}</strong> with photos and details documented during your move.
          Once you accept, it belongs to your account permanently.
        </p>

        {info.item_count > 50 && (
          <div style={{
            background: '#fff7ed', borderLeft: '4px solid #f97316',
            padding: '12px 16px', borderRadius: '0 8px 8px 0',
            marginBottom: '24px', textAlign: 'left',
          }}>
            <p style={{ margin: '0 0 4px', fontWeight: 600, fontSize: '13px', color: '#92400e' }}>
              A paid plan is needed for this collection
            </p>
            <p style={{ margin: 0, fontSize: '13px', color: '#78350f', lineHeight: 1.5 }}>
              Free accounts hold up to 50 items. This collection has {info.item_count} items, so you'll need
              {info.item_count <= 1000 ? ' a Basic plan ($12/yr)' : info.item_count <= 5000 ? ' a Pro plan ($36/yr)' : ' a Business plan'} to access everything.
              You can claim now and upgrade right after — your inventory will be waiting.
            </p>
          </div>
        )}

        {error && (
          <p style={{ color: '#e53e3e', fontSize: '13px', marginBottom: '16px' }}>{error}</p>
        )}

        {isAuthenticated ? (
          <button
            onClick={handleAccept}
            disabled={accepting}
            style={{
              width: '100%', padding: '14px', background: '#007AFF', color: '#fff',
              border: 'none', borderRadius: '10px', fontSize: '16px', fontWeight: 700,
              cursor: accepting ? 'not-allowed' : 'pointer', opacity: accepting ? 0.7 : 1,
              marginBottom: '12px',
            }}
          >
            {accepting ? 'Claiming…' : 'Accept & Claim Collection'}
          </button>
        ) : (
          <>
            <button
              onClick={handleSignUpThenAccept}
              style={{
                width: '100%', padding: '14px', background: '#007AFF', color: '#fff',
                border: 'none', borderRadius: '10px', fontSize: '16px', fontWeight: 700,
                cursor: 'pointer', marginBottom: '12px',
              }}
            >
              Create Free Account & Claim
            </button>
            <button
              onClick={handleSignUpThenAccept}
              style={{
                width: '100%', padding: '12px', background: 'transparent', color: '#007AFF',
                border: '1.5px solid #007AFF', borderRadius: '10px', fontSize: '15px',
                fontWeight: 600, cursor: 'pointer',
              }}
            >
              Sign In to Existing Account
            </button>
          </>
        )}

        <p style={{ color: '#999', fontSize: '12px', marginTop: '16px' }}>
          Invitation expires {expiresDate} · Sent to {info.to_email}
        </p>
      </div>
    </div>
  );
};

export default CollectionTransferPage;
